// @file Composable that loads comments for posts when they enter the viewport using intersection observer.
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { canObserveIntersection, observeElementIntersection } from '@@/bits/intersection'
import { useCommentsStore } from '@@/pinia/surface_comments'
import type { Post } from '@@/types'
import type { Ref } from 'vue'
import { onBeforeUnmount, onMounted } from 'vue'

/**
 * Composable that automatically loads comments for a post when it enters the viewport.
 * Uses IntersectionObserver when available, with fallbacks for unsupported browsers.
 *
 * Features:
 * - Lazy loads comments only when post becomes visible
 * - Avoids duplicate fetches if comments are already loaded
 * - Handles cleanup on component unmount
 * - Special handling for Safari in iframes
 *
 * @param {Object} params - Parameters object
 * @param {Post} params.post - The post object to load comments for
 * @param {Ref<HTMLElement | undefined>} params.postElement - Vue ref to the post DOM element to observe
 * @returns {void}
 */
export function useIntersectionCommentLoader({
  post,
  postElement,
}: {
  post: Post
  postElement: Ref<HTMLElement | undefined>
}): void {
  if (!isAppUsing('surfaceCommentsPerPost')) return

  const commentsStore = useCommentsStore()
  const { fetchComments } = commentsStore

  let observer: IntersectionObserver | null = null

  const isDisplayedInIframe = window.self !== window.top
  const isIframeInSafari = isDisplayedInIframe && (device.safari || device.ios)

  // Helper to cleanup observer
  const cleanupObserver = (): void => {
    if (observer != null) {
      observer.disconnect()
      observer = null
    }
  }

  onMounted(() => {
    if (post.id == null || post.hashid == null || postElement.value == null) return

    // Early return if we already have comments for this post
    if (commentsStore.commentIdsByPost[post.id]?.length > 0) return

    // For browsers that don't support IntersectionObserver or Safari iframes, fetch immediately
    if (!canObserveIntersection || isIframeInSafari) {
      fetchComments({ wishId: post.id, wishHashid: post.hashid })
      return
    }

    observer = observeElementIntersection(postElement.value, () => {
      if (post.id == null || post.hashid == null) return
      fetchComments({ wishId: post.id, wishHashid: post.hashid })
      cleanupObserver()
    })
  })

  onBeforeUnmount(() => {
    cleanupObserver()
  })
}
